import { fetchDataFromBackend } from "../api/api";
import UserCalendar from "../DTOs/weekTemplateDTO";
import logger from "../core/utils/logger";

const isSimulation= import.meta.env.VITE_BACKEND_DATA_SIMULATION === "true";
// Helper to generate fake data
// Helper function: generate simulation data
const generateSimulatedUserCalendars = (count = 20) => {
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    const now = new Date();

    return Array.from({ length: count }).map((_, index) => {
        const userId = `user-${index + 1}`;
        const name = `Sim User ${index + 1}`;
        const email= `SimUser${index + 1}@test.com`;

        const calendarTemplate = {
            id: index + 1000,
            year: now.toISOString(),
            workTimeHours: 40,
            workTimeMinutes: 0,
            weekPlan: {
                dailyPlans: daysOfWeek.map((dayName, i) => ({
                    id: `${userId}-dp-${i + 1}`,
                    day: dayName,
                    plan: {
                        id: `${userId}-plan-${i + 1}`,
                        dayStatus: 0,
                        workDurations: [
                            {
                                id: `${userId}-wd-${i + 1}`,
                                startTime: new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, 9, 0).toISOString(),
                                endTime: new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, 17, 0).toISOString(),
                            }
                        ]
                    }
                }))
            }
        };

        return new UserCalendar({
            id: index + 1,
            organizationId: `org-${(index % 3) + 1}`,
            userId,
            name,
            email,
            calendarTemplates: [calendarTemplate]
        });
    });
};


// Build the 26.5 list/search query string from filter args. Only non-empty
// filters are sent; `validityCoversDate` is a date-only string (YYYY-MM-DD),
// never a shift-time UTC value, so the backend's "covers" rule stays calendar-exact.
const buildTemplateQuery = ({ name, status, validityCoversDate, includeArchived } = {}) => {
    const params = new URLSearchParams();
    if (name) params.set("name", name);
    if (status) params.set("status", status);
    if (validityCoversDate) params.set("validityCoversDate", validityCoversDate);
    if (includeArchived) params.set("includeArchived", "true");
    const qs = params.toString();
    return qs ? `?${qs}` : "";
};

export const WeekTemplateService = {
    // 27.1/27.4: list & search. Optional filters map 1:1 to the 26.5 query params.
    async getUserWeekTemplateSchedules(filters = {}) {
        try {
            if (isSimulation) {
                logger.log("Simulation mode: returning simulated user week templates.");
                const simulated = generateSimulatedUserCalendars(20);
                logger.log("Simulated templates:", JSON.stringify(simulated[0], null, 2));
                return simulated;
            }
            const query = buildTemplateQuery(filters);
            const response = await fetchDataFromBackend(`/WeekTemplate/GetEmployeeTemplateByOrg${query}`, {skipAuth: false, method: 'get'});
            logger.log("User week template schedules response:", JSON.stringify(response));
            var result = response.map(item=>new UserCalendar(item));
            return result;
        } catch (error) {
            logger.error("Error getting user week template schedules:", error);
            throw error;
        }
    },
    async saveUserWeekTemplateSchedule(userWeekTemplateSchedule) {
        try {
            logger.log("User week template schedule:", JSON.stringify(userWeekTemplateSchedule));
            if (isSimulation) {
                logger.log("Simulation mode: skipping save.");
                return { success: true, message: "Simulated save successful" };
            }
            const response = await fetchDataFromBackend("/WeekTemplate/InsertEmployeeTemplate", {skipAuth: false, method: 'post', data: userWeekTemplateSchedule});

            //var result = response.map(item=>new UserCalender(item));
            return response;
        } catch (error) {
            logger.error("Error saving user week template schedule:", error);
            throw error;
        }
    },
    async deleteUserWeekTemplateSchedule(userWeekTemplateScheduleId) {
        try {
            if (isSimulation) {
                logger.log(`Simulation mode: skipping delete for schedule ID ${userWeekTemplateScheduleId}`);
                return { success: true, message: "Simulated delete successful" };
            }
            const response = await fetchDataFromBackend(`/WeekTemplate/DeleteEmployeeTemplate/${userWeekTemplateScheduleId}`, {
                skipAuth: false,
                method: 'delete'

              });
            return response;
        } catch (error) {
            logger.error("Error deleting user week template schedule:", error);
            throw error;
        }
    },

    // ---------------------------------------------------------------------
    // Epic 27: per-CalendarTemplate management (26.2/26.3/26.4 contract).
    // These hit the dedicated WeekTemplate routes so a single template can be
    // created/edited/defaulted/archived without re-saving the whole employee
    // aggregate (which is what the legacy InsertEmployeeTemplate upsert does).
    // ---------------------------------------------------------------------

    // 26.2: create one calendar template under an existing EmployeeTemplate.
    // CreatedAt is server-set; dto carries name + date-only validFrom/validTo.
    async createTemplate(employeeTemplateId, dto) {
        const response = await fetchDataFromBackend(
            `/WeekTemplate/CreateCalendarTemplate/${employeeTemplateId}`,
            { skipAuth: false, method: 'post', data: dto });
        return response;
    },

    // 26.2: by-id update of a single calendar template (name/validity/pattern/status).
    async updateTemplate(templateId, dto) {
        const response = await fetchDataFromBackend(
            `/WeekTemplate/UpdateCalendarTemplate/${templateId}`,
            { skipAuth: false, method: 'put', data: dto });
        return response;
    },

    // 26.3: there is no dedicated activate/deactivate route — IsActive is toggled
    // through the by-id update (which re-checks the overlap invariant server-side).
    // The caller passes the current full DTO with isActive flipped.
    async setTemplateStatus(templateId, dto) {
        return this.updateTemplate(templateId, dto);
    },

    // 26.3: set this template as the employee's single default.
    async setTemplateDefault(templateId) {
        const response = await fetchDataFromBackend(
            `/WeekTemplate/SetDefaultCalendarTemplate/${templateId}`,
            { skipAuth: false, method: 'put' });
        return response;
    },

    // 26.4: bulk archive (soft-delete) by ids. Body is a raw int array.
    async archiveTemplates(ids) {
        const response = await fetchDataFromBackend(
            `/WeekTemplate/ArchiveCalendarTemplates`,
            { skipAuth: false, method: 'put', data: ids });
        return response;
    },

    // 26.4: bulk unarchive (restore) by ids. Body is a raw int array.
    async unarchiveTemplates(ids) {
        const response = await fetchDataFromBackend(
            `/WeekTemplate/UnarchiveCalendarTemplates`,
            { skipAuth: false, method: 'put', data: ids });
        return response;
    },

    // 26.x: hard-delete a single calendar template row (distinct from the legacy
    // whole-employee delete). Blocked server-side while it is the default.
    async deleteTemplate(templateId) {
        const response = await fetchDataFromBackend(
            `/WeekTemplate/DeleteCalendarTemplate/${templateId}`,
            { skipAuth: false, method: 'delete' });
        return response;
    }
};
